import { AlertTriangle, RotateCw } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Contains a page that throws while rendering.
 *
 * Without it React unmounts the whole tree on any render error and the user
 * sees a blank screen with no way back. Placed around the routed page, it keeps
 * the sidebar and header working and says what happened. It resets on
 * navigation because the layout remounts the page container per path.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Page failed to render', error, info.componentStack);
  }

  render() {
    if (this.state.error === null) {
      return this.props.children;
    }

    return (
      <div role="alert" className="mx-auto max-w-[560px] rounded-xl border border-line bg-card px-6 py-12 text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-danger-soft text-danger">
          <AlertTriangle className="h-6 w-6" aria-hidden="true" />
        </div>
        <h2 className="text-base font-semibold text-ink">This page couldn&apos;t be displayed</h2>
        <p className="mx-auto mt-1.5 max-w-sm text-sm text-muted">
          Something on it failed to show. The rest of the application still works — reload to try again, or go
          back to the dashboard.
        </p>
        <p className="mx-auto mt-3 max-w-sm break-words font-mono text-xs text-subtle">{this.state.error.message}</p>
        <div className="mt-5 flex flex-wrap justify-center gap-2">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-1.5 rounded-md bg-accent px-3.5 py-2 text-sm font-medium text-accent-fg hover:bg-accent-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--sms-accent-ring)]"
          >
            <RotateCw className="h-4 w-4" aria-hidden="true" />
            Reload
          </button>
          <a
            href="/"
            className="inline-flex items-center rounded-md border border-line bg-card px-3.5 py-2 text-sm font-medium text-ink hover:bg-surface focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--sms-accent-ring)]"
          >
            Back to Dashboard
          </a>
        </div>
      </div>
    );
  }
}
